import { ConnectionType, TEvent } from "protico-sdk";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../hooks";
import {
  ContactPlus,
  useConnectionCheck,
} from "../../../hooks/useConnectionCheck";
import useSocialConnection from "../../../hooks/useSocialConnection";
import "../index.css";
import FriendItem from "./Item";
function getLatestEvents(events: TEvent[]): TEvent[] {
  // Sort the events by created_at in descending order
  const sortedEvents = events.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  // Reduce the sorted events to get the latest event for each target
  const latestEventsMap = sortedEvents.reduce((acc, event) => {
    if (!acc.has(event.target)) {
      acc.set(event.target, event);
    }
    return acc;
  }, new Map<string, TEvent>());

  // Convert the map values to an array
  return Array.from(latestEventsMap.values());
}

const FriendList = ({ modalType }: { modalType: ConnectionType }) => {
  const { t } = useTranslation();
  const { events } = useAppSelector((state) => state.event);
  const { getConnectionVerificationBatch } = useConnectionCheck();
  const { contacts } = useSocialConnection();
  const [contactsPlus, setContactsPlus] = useState<ContactPlus[]>([]);
  const [isContactsFetching, setIsContactsFetching] = useState(true);
  useEffect(() => {
    setIsContactsFetching(true);
    getConnectionVerificationBatch().then((res) => {
      const sortedContactsPlus = res.sort((a, b) => {
        // Put Waiting on top and block at bottom
        if (a.connectionState !== b.connectionState) {
          if (a.connectionState === 2 || b.connectionState === 3) return 1;
          if (b.connectionState === 2 || a.connectionState === 3) return -1;
        }
        // Sort alphabetically
        const an = a.name.toLowerCase();
        const bn = b.name.toLowerCase();
        if (an < bn) return -1;
        if (an > bn) return 1;
        return 0;
      });
      setContactsPlus(sortedContactsPlus);
      setIsContactsFetching(false);
    });
  }, [JSON.stringify(contacts)]);

  const getview = useMemo(() => {
    if (isContactsFetching)
      return (
        <div className="body-container">
          <div className="dot-falling" />
        </div>
      );
    let elements: Array<JSX.Element> = [];

    if (modalType === ConnectionType.CONNECT) {
      const eventElements = getEventElements(contactsPlus, events);
      eventElements.forEach((el, index) => {
        elements.push(
          <FriendItem
            key={`FriendItem_event_${index}`}
            targetAddress={el.target}
            isRequest
          />
        );
      });
    }
    const contactElements = contactsPlus
      .filter((contact) => contact.connectionType === modalType)
      .map((contact, index) => {
        return (
          <FriendItem
            key={`FriendItem_${index}`}
            targetAddress={contact.target}
          />
        );
      });

    contactElements.forEach((element) => {
      elements.push(element);
    });
    if (elements.length === 0)
      return (
        <div className="body-container">
          {t(modalType === "block" ? "DM_No_Block_List" : "DM_No_Data")}
        </div>
      );
    return elements;
  }, [isContactsFetching, contactsPlus, modalType]);

  return <React.Fragment>{getview}</React.Fragment>;
};
export default FriendList;

const getEventElements = (
  userContacts: ContactPlus[],
  events: Array<TEvent>
) => {
  const relatedAddresses = userContacts.map((contact) =>
    contact.target.toLowerCase()
  );
  const sortedEvents = [...events].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  let filteredEvents = new Map<string, TEvent>();
  sortedEvents.forEach((event) => {
    if (
      event.action === "requested" &&
      !relatedAddresses.includes(event.target.toLowerCase())
    ) {
      if (!filteredEvents.has(event.target.toLowerCase())) {
        filteredEvents.set(event.target.toLowerCase(), event);
      }
    }
  });

  return Array.from(filteredEvents.values());
};
